import { Occupancy, VisitorInside } from '../../../interfaces/hierarchy-interfaces';

/**
 * WHICH OPEN ARRIVAL BELONGS TO WHICH RESIDENT.
 *
 * ══════════════════ THE BUG THIS MODULE EXISTS TO KILL ══════════════════
 *
 * The Resident in/out screen used to build this:
 *
 *     const map = new Map<number, VisitorInside>();
 *     inside.filter(v => v.visitor_type === 'Resident Entry' && v.unit_id != null)
 *           .forEach(v => { if (!map.has(v.unit_id)) map.set(v.unit_id, v); });
 *
 * FIRST ROW WINS, PER UNIT. A unit holds a household, and a household is
 * routinely more than one person: a couple in 4B both walk in, the desk logs two
 * Resident Entry rows against unit 4B, and every resident of 4B then resolves to
 * the SAME arrival — the first one. Pressing "Sign out" beside the second person
 * closed the FIRST person's arrival. The screen then reloaded, the remaining
 * open row was picked up by whoever was listed first, and the two swapped.
 *
 * Nothing about that is visible at the desk. Both names are on the roster, both
 * rows say "in since 10:37", and the departure that was written is attributed to
 * a real person who really was inside — just not the one the concierge pressed.
 * It is also exactly what "clicking too fast on outs put the wrong state on the
 * wrong person" looks like from the outside, which is why the report blamed
 * timing: the row you press and the row that closes genuinely do disagree.
 *
 * ══════════════════ WHY MATCHING ON THE UNIT AT ALL ══════════════════
 *
 * Because it is the only reliable link. Visitor names are AES-GCM with a random
 * IV (migration 040), so no server predicate can match one; the presence payload
 * carries `people` already decrypted and that is the only readable copy. The
 * unit is a foreign key and the name is free text somebody typed, so the unit
 * NARROWS and the name DISAMBIGUATES — in that order, never the other way.
 *
 * ══════════════════ THE THREE OUTCOMES, AND WHY THE THIRD IS NOT A BUTTON ══
 *
 *   matched     exactly one open arrival in this unit carries this person's
 *               name. The overwhelmingly common case, and the only one where a
 *               single tap can be certain of what it closes.
 *   sole        no name matched, and the unit holds exactly ONE open arrival.
 *               Then there is nothing to be wrong about: the arrival is the
 *               unit's and this is a resident of it. Keeps the screen working
 *               for an entry logged with a typo, an initial, or no name at all.
 *   ambiguous   the unit holds SEVERAL open arrivals and none of them carries
 *               this person's name. There is no answer here, and the old code
 *               invented one. The row says the unit has somebody inside and
 *               sends the desk to "Coming out", where arrivals are listed as
 *               themselves rather than through a resident.
 *
 * AN ARRIVAL IS CLAIMED AT MOST ONCE. Two residents of one unit with two open
 * arrivals get one each; they can never both resolve to the same row, which is
 * the whole defect restated as a post-condition.
 */

/** The desk's own kind for "somebody who lives here walked in or out". */
export const RESIDENT_ENTRY = 'Resident Entry';

export type PresenceState = 'matched' | 'sole' | 'ambiguous' | 'out';

export interface ResidentPresence {
  /** The arrival to close, or null when there is no safe answer. */
  entry: VisitorInside | null;
  state: PresenceState;
}

const norm = (s: string): string =>
  s.trim().toLowerCase().replace(/\s+/g, ' ');

const fullName = (p: Occupancy): string =>
  norm(`${p.first_name || ''} ${p.last_name || ''}`);

/**
 * True when an arrival's recorded people include this resident.
 *
 * BOTH DIRECTIONS OF `includes`, because the two strings are written by
 * different hands. The desk types "Ana Marie Kovac" on the arrival while the
 * occupancy row says "Ana Kovac", or the other way round. An exact equality test
 * would drop straight through to `ambiguous` for a household whose names ARE
 * distinguishable, which is a worse answer than the one it is protecting.
 */
const namesPerson = (entry: VisitorInside, person: Occupancy): boolean => {
  const want = fullName(person);
  if (!want) return false;
  const last = norm(person.last_name || '');
  const first = norm(person.first_name || '');

  return (entry.people || []).some(raw => {
    const got = norm(raw);
    if (!got) return false;
    if (got === want || got.includes(want) || want.includes(got)) return true;

    // A household shares a surname, so the surname alone can NEVER decide a
    // match — it would hand one arrival to both halves of a couple, which is
    // the defect. A first name can, and is what the desk actually types.
    return !!first && first.length >= 2 && (got === first || got.startsWith(first + ' ')) && !!last;
  });
};

/**
 * Resolve every resident to at most one open arrival.
 *
 * PURE, and exported separately from the screen for the reason the sizing guard
 * is a source test: this is where the wrong-person departure came from, and a
 * render assertion cannot tell "closed the right row" from "closed a row".
 */
export const resolvePresence = (
  residents: Occupancy[],
  inside: VisitorInside[]
): Map<string, ResidentPresence> => {
  const open = (inside || []).filter(
    v => v.visitor_type === RESIDENT_ENTRY && v.unit_id != null
  );

  const byUnit = new Map<number, VisitorInside[]>();
  for (const v of open) {
    const unit = v.unit_id as number;
    const list = byUnit.get(unit);
    if (list) list.push(v);
    else byUnit.set(unit, [v]);
  }

  const out = new Map<string, ResidentPresence>();
  // An arrival, once given to somebody, is theirs. Without this two residents of
  // one unit can both be handed the same row again, by a different route.
  const claimed = new Set<number>();

  // PASS 1 — NAME MATCHES FIRST, ACROSS EVERYBODY. Doing name and fallback in
  // one loop would let the first resident in the list take a unit's only arrival
  // by the fallback rule before the person actually named on it was reached.
  for (const person of residents || []) {
    const list = byUnit.get(person.unit_id) || [];
    const hits = list.filter(v => !claimed.has(v.id) && namesPerson(v, person));
    if (hits.length === 1) {
      claimed.add(hits[0].id);
      out.set(person.id, { entry: hits[0], state: 'matched' });
    }
  }

  // PASS 2 — the unit's ONE remaining arrival, for anybody still unresolved.
  for (const person of residents || []) {
    if (out.has(person.id)) continue;
    const list = (byUnit.get(person.unit_id) || []).filter(v => !claimed.has(v.id));
    if (list.length === 0) {
      out.set(person.id, { entry: null, state: 'out' });
    } else if (list.length === 1) {
      claimed.add(list[0].id);
      out.set(person.id, { entry: list[0], state: 'sole' });
    } else {
      out.set(person.id, { entry: null, state: 'ambiguous' });
    }
  }

  return out;
};

/**
 * HOW MANY RESIDENTS ARE ACTUALLY IN.
 *
 * Counted off the RESOLVED arrivals, not off "this unit has one open row". The
 * old count asked the unit and so reported a two-person household as two people
 * inside when the desk had logged one of them — the same first-row-wins map,
 * producing a number instead of a wrong departure.
 */
export const insideCountOf = (
  presence: Map<string, ResidentPresence>
): number => {
  let n = 0;
  presence.forEach(p => {
    if (p.entry) n += 1;
  });

  return n;
};
